import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SPACING } from "../theme/constants";
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  logout,
  updateUserProfile,
  changeUserPassword,
} from "../redux/slices/userSlice";
import { allSites } from "../redux/slices/siteSlice";
import { fetchMyPlan } from "../redux/slices/planSlice";
import {
  ProfileInformationCard,
  ChangePasswordCard,
  AccountStatsCard,
  AccountMetadataCard,
} from "../components/userProfile";

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user, loading, isAuthenticated, error } = useSelector(
    (state) => state.user,
  );
  const { site: siteData } = useSelector((state) => state.site);
  const { myPlan, usage } = useSelector((state) => state.plan);

  const [successMessage, setSuccessMessage] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    dispatch(allSites());
    dispatch(fetchMyPlan());
  }, [dispatch]);

  const handleUpdateProfile = async (data) => {
    try {
      await dispatch(updateUserProfile(data)).unwrap();
      setSuccessMessage("Profile updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      throw error;
    }
  };

  const handleChangePassword = async (data) => {
    try {
      await dispatch(changeUserPassword(data)).unwrap();
      setPasswordSuccess("Password changed successfully!");
      setTimeout(() => setPasswordSuccess(""), 3000);
    } catch (error) {
      throw error;
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    setTimeout(() => navigate("/auth"), 100);
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (!user || !isAuthenticated) {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography variant="h6" color="text.secondary">
          No user data found. Please log in.
        </Typography>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate("/auth")}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: SPACING.lg,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            User Profile
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View and manage your account details
          </Typography>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: SPACING.md, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {passwordSuccess && (
        <Alert severity="success" sx={{ mb: SPACING.md, borderRadius: 2 }}>
          {passwordSuccess}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: SPACING.md, borderRadius: 2 }}>
          {error.message || error}
        </Alert>
      )}

      <Grid container spacing={SPACING.md}>
        <Grid size={{ xs: 12 }}>
          <ProfileInformationCard
            user={user}
            onUpdate={handleUpdateProfile}
            onLogout={handleLogout}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ChangePasswordCard onChangePassword={handleChangePassword} />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <AccountStatsCard
            stats={{
              ...user?.stats,
              totalSites:
                siteData?.pagination?.totalCount ??
                siteData?.sites?.length ??
                user?.stats?.totalSites ??
                0,
            }}
            plan={myPlan}
            usage={usage}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <AccountMetadataCard user={user} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserProfile;
